import React, {
  DetailedHTMLProps,
  SelectHTMLAttributes,
  forwardRef,
  useMemo,
} from 'react';
import { useIntl } from 'react-intl';
import { useFocusRing } from '@react-aria/focus';
import { mergeProps } from '@react-aria/utils';
import classnames from 'classnames';
import { LgChevronDownIcon as ChevronDownIcon } from '@stpkg/stand-react';

import { useAfterMountedOnBrowser } from '../../hooks/helpers/useAfterMountedOnBrowser';

export type RSSelectProps = DetailedHTMLProps<
  SelectHTMLAttributes<HTMLSelectElement>,
  HTMLSelectElement
> & {
  labelText: string;
  hasSelectedValue?: boolean;
  isBlock?: boolean;
  isError?: boolean;
};

export const RSSelect = forwardRef<HTMLSelectElement, RSSelectProps>(
  ({ labelText, hasSelectedValue, isBlock, isError, ...props }, ref) => {
    const { disabled, required } = props;

    const intl = useIntl();
    const { isFocusVisible, focusProps } = useFocusRing();

    const color = '#0066FF';

    const isTransitionReady = useAfterMountedOnBrowser();

    const transitionClasses = useMemo(() => {
      if (!isTransitionReady) {
        return undefined;
      }

      return ['transition'];
    }, [isTransitionReady]);

    const labelTextColorClasses = useMemo(() => {
      if (disabled) {
        if (!hasSelectedValue) {
          return ['text-gray-200'];
        } else {
          return ['text-gray-400'];
        }
      }

      if (isError) {
        return ['text-red-700'];
      }

      if (!hasSelectedValue) {
        return ['text-gray-400'];
      }

      return ['text-gray-1000'];
    }, [hasSelectedValue, isError, disabled]);

    const bgColorClasses = useMemo(() => {
      if (disabled) {
        return ['bg-gray-10'];
      }

      if (isError) {
        return ['bg-red-10'];
      }

      return ['bg-white'];
    }, [isError, disabled]);

    return (
      <>
        <div
          className={classnames(
            'root',
            isFocusVisible && 'focus-visible',
            'relative',
            'box-border',
            !isBlock ? 'inline-block' : 'block w-full',
            'h-56',
            'rounded-15',
            bgColorClasses,
            isError && !disabled ? 'ring-red-700' : 'ring-gray-200',
            'ring-1',
            'transition-all',
            isFocusVisible && 'bg-white',
            isFocusVisible && 'ring-2',
          )}
        >
          <label
            className={classnames(
              'box-border',
              'block',
              'w-full',
              'h-full',
              !disabled ? 'cursor-pointer' : 'cursor-not-allowed',
              'rounded-15',
            )}
          >
            <select
              {...mergeProps(props, focusProps)}
              ref={ref}
              className={classnames(
                'rs-font',
                'appearance-none',
                'box-border',
                'block',
                'w-full',
                'h-full',
                'pt-2.5',
                'pr-6',
                'pb-[6px]',
                'pl-2',
                'bg-transparent',
                'text-16',
                'pc:text-15',
                !disabled ? 'text-gray-1000' : 'text-gray-400',
                'font-normal',
                'border-none',
                'outline-none',
                'focus:outline-none',
                !disabled ? 'cursor-pointer' : 'cursor-not-allowed',
                'rounded-15',
                'transition',
              )}
            />
            <span
              className={classnames(
                'rs-font',
                'absolute',
                'top-0',
                'left-2',
                !hasSelectedValue ? 'scale-100' : 'scale-[0.66]',
                !hasSelectedValue
                  ? ['translate-y-[15px]', 'pc:translate-y-[16px]']
                  : 'translate-y-[9px]',
                'origin-top-left',
                'select-none',
                'text-16',
                'pc:text-15',
                labelTextColorClasses,
                'font-normal',
                'pointer-events-none',
                transitionClasses,
              )}
            >
              {labelText}
              {required && (
                <span className="text-red-700">
                  {intl.formatMessage({
                    defaultMessage: '（必須）',
                    id: 'QMnLcU',
                    description:
                      'src/reservation-page/forms/RSSelect/index.tsx',
                  })}
                </span>
              )}
            </span>
            <div className="pointer-events-none absolute right-2 top-0 flex h-full items-center">
              <ChevronDownIcon
                className={classnames(
                  isError && !disabled && !isFocusVisible
                    ? 'fill-red-700'
                    : 'fill-gray-400',
                )}
                width="20"
                height="20"
                viewBox="0 0 48 48"
              />
            </div>
          </label>
        </div>
        <style jsx>{`
          .root.focus-visible {
            --tw-ring-color: ${color};
          }
        `}</style>
      </>
    );
  },
);

RSSelect.displayName = 'RSSelect';
